import type { ReactiveHead } from '@unhead/vue';

export default function useSeo({
  siteDescription,
  siteName,
  pageName,
  domain,
  image,
  title,
  description,
  path,
  includeCanonical = true,
  includeSchema = true,
  includeOgTags = true,
  ogLocale = 'nl_NL',
}: {
  siteDescription: string;
  siteName: string;
  pageName: string;
  domain: string;
  image?: string | null;
  title: string;
  description: string;
  path: string;
  includeCanonical?: boolean;
  includeSchema?: boolean;
  includeOgTags?: boolean;
  ogLocale?: string;
}) {
  const canonicalUrl = `${domain}${path}`;
  const meta: ReactiveHead['meta'] = [
    {
      name: 'description',
      content: () => description,
      tagPriority: -10,
    },
  ];

  if (includeOgTags) {
    meta.push(
      {
        property: 'og:url',
        content: () => canonicalUrl,
        tagPriority: -10,
      },
      {
        property: 'og:site_name',
        content: () => siteName,
        tagPriority: -10,
      },
      {
        property: 'og:type',
        content: () => 'website',
        tagPriority: -10,
      },
      {
        property: 'og:title',
        content: () => title,
        tagPriority: -10,
      },
      {
        property: 'og:description',
        content: () => description,
        tagPriority: -10,
      },
      {
        property: 'og:locale',
        content: () => ogLocale,
        tagPriority: -10,
      },
    );

    // We only want to add the image meta if it exists
    if (image) {
      // Serve the og:image from our Akamai proxy instead of the Storyblok CDN,
      // so crawler and link-preview fetches don't consume Storyblok quota
      const proxiedImage = mapStoryblokAssetUrlToAkamai(image);

      meta.push({
        property: 'og:image',
        content: () => proxiedImage,
        tagPriority: -10,
      });
    }
  }

  useHead({
    title: {
      textContent: () => title,
      tagPriority: -10,
    },
    link: includeCanonical
      ? [
          {
            rel: 'canonical',
            href: canonicalUrl,
            tagPriority: -10,
          },
        ]
      : [],
    script: includeSchema
      ? [
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(buildSchema({ domain, siteName, siteDescription, pageName })),
          },
        ]
      : [],
    meta,
  });
}
